/**
 * BillableItemReceiptUpload — Phase 10b.2 Expense Receipt Uploads
 *
 * Drag-and-drop receipt file upload for billable_expense items.
 * Renders only when item.category === 'billable_expense'.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { Paperclip, Trash2, FileText, Image, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { compressImage } from '@/lib/photo/imageCompression';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp', 'application/pdf'];
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

interface ReceiptFile {
  id: string;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_by: string;
  created_at: string;
}

interface Props {
  billableItemId: string;
  /** Called after files are uploaded or deleted so parent can refresh counts */
  onFilesChange?: () => void;
}

async function getAuthToken(): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function BillableItemReceiptUpload({ billableItemId, onFilesChange }: Props) {
  const [files, setFiles] = useState<ReceiptFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFiles = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/billable-items/${billableItemId}/files`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load files');
      const data = await res.json();
      setFiles(data.files || []);
    } catch (e) {
      // Non-blocking: files just won't show
      console.warn('Failed to load receipt files:', e);
    }
  }, [billableItemId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const uploadFile = async (file: File) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`"${file.name}" is not an accepted file type (JPEG, PNG, HEIC, WEBP, PDF only)`);
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setError(`"${file.name}" exceeds the ${MAX_SIZE_MB}MB limit (${formatBytes(file.size)})`);
      return;
    }
    if (file.size === 0) {
      setError(`"${file.name}" is empty`);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/billable-items/${billableItemId}/files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.status === 413) throw new Error(`File too large (max ${MAX_SIZE_MB}MB)`);
      if (res.status === 400) {
        const data = await res.json();
        throw new Error(data.detail || 'File rejected');
      }
      if (!res.ok) throw new Error('Upload failed');
    } catch (e) {
      throw e;
    }
  };

  const handleFiles = async (fileList: FileList | File[]) => {
    const toUpload = Array.from(fileList);
    if (!toUpload.length) return;

    setError(null);
    setUploading(true);

    for (let i = 0; i < toUpload.length; i++) {
      const file = toUpload[i];
      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        setUploadProgress(`Compressing ${file.name}...`);
        const result = await compressImage(file, (pct) => {
          setUploadProgress(`Compressing ${file.name}... ${pct}%`);
        });
        fileToUpload = result.file;
      }
      setUploadProgress(`Uploading ${i + 1} of ${toUpload.length}: ${file.name}`);
      try {
        await uploadFile(fileToUpload);
      } catch (e) {
        setError((e as Error).message);
        setUploading(false);
        setUploadProgress(null);
        return;
      }
    }

    setUploading(false);
    setUploadProgress(null);
    await loadFiles();
    onFilesChange?.();
  };

  const handleDelete = async (fileId: string, fileName: string) => {
    if (!confirm(`Delete "${fileName}"?`)) return;
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/billable-item-files/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok && res.status !== 204) throw new Error('Delete failed');
      setFiles(prev => prev.filter(f => f.id !== fileId));
      onFilesChange?.();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleDownload = async (fileId: string, fileName: string) => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/billable-item-files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const isImage = (contentType: string) =>
    contentType.startsWith('image/');

  return (
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Paperclip className="h-4 w-4" />
          Receipts
          {files.length > 0 && (
            <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              {files.length}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Drop Zone */}
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`
            flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed
            p-6 text-center cursor-pointer transition-colors
            ${isDragging
              ? 'border-blue-400 bg-blue-50'
              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }
            ${uploading ? 'pointer-events-none opacity-60' : ''}
          `}
        >
          {uploading ? (
            <>
              <Upload className="h-6 w-6 animate-pulse text-slate-400" />
              <p className="text-sm text-slate-500">{uploadProgress}</p>
            </>
          ) : (
            <>
              <Upload className="h-6 w-6 text-slate-400" />
              <p className="text-sm text-slate-600">
                Drop receipts here or <span className="text-blue-600 underline">browse</span>
              </p>
              <p className="text-xs text-slate-400">JPEG, PNG, HEIC, WEBP, PDF — max {MAX_SIZE_MB}MB each</p>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={e => e.target.files && handleFiles(e.target.files)}
        />

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
            <X className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* File List */}
        {files.length > 0 && (
          <ul className="space-y-2">
            {files.map(f => (
              <li
                key={f.id}
                className="flex items-center gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2"
              >
                {isImage(f.content_type) ? (
                  <Image className="h-4 w-4 shrink-0 text-slate-400" />
                ) : (
                  <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                )}
                <button
                  onClick={() => handleDownload(f.id, f.file_name)}
                  className="flex-1 truncate text-left text-sm text-blue-600 hover:underline"
                >
                  {f.file_name}
                </button>
                <span className="shrink-0 text-xs text-slate-400">{formatBytes(f.file_size)}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-slate-400 hover:text-red-500"
                  onClick={() => handleDelete(f.id, f.file_name)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
